import * as React from 'react';
import { cloneDeep, debounce } from 'lodash';
import { tz } from 'moment-timezone';
import { Prompt, RouteComponentProps } from 'react-router-dom';
import {
  NiForm,
  NiSelect,
  NiModal,
  HelperPopover,
  NiRadio
} from '@component/index';
import {
  API,
  idCreateRegex,
  AGENT_MODE,
  AGENT_PRIVACY,
  AGENT_LAN,
  AGENT_DEPLOY_MODE,
  DEFAULT_TIMEZONE,
  AGENT_REGION,
  SelectionOption,
  getSelectionOptions,
  SELECTION_KEY,
  agentLabelRegex
} from '@config/index';
import {
  ProviderType,
  AgentInfo,
  agentToLocal,
  OrgInfo,
  orgToLocal
} from '@interface/index';
import { DeleteOutlined } from '@ant-design/icons';
import { Input, message, Upload, Space, Switch, UploadFile, Checkbox, Typography } from 'antd';
import { localizedString } from '@locale/index';
import { getAgentLangFromLangLabel, getOrgId, getOrgName, getStructAgentId, isTemplateOrg } from '@lib/index';
import '@style/agent/create.less';
import axios from 'axios';
const { Paragraph } = Typography;

const DefaultTemplateName = 'DefaultApplication';

export type CreatePropsInfo = RouteComponentProps<{}> & {
  type: 'component' | 'chatbot' | 'backend' | 'knowledge' | 'all';
  visible: boolean;
  isCloning?: boolean;
  didCloseClick?: () => void;
  cloneAgent?: AgentInfo;
  cloneToOrgId?: string;
  cloneToOrgName?: string;
};

export interface CreateStateInfo {
  createMode: 'create' | 'clone' | 'openapi';
  orgId?: string;
  orgOptions?: SelectionOption[];
  name: string;
  agentMode: string;
  agentLanguage?: string;
  languages: string[];
  loading: boolean;
  backendProviderType?: ProviderType;
  privacy: string;
  isInterface: boolean;
  isService?: boolean;
  isServiceImplementation?: boolean;
  templateRes?: AgentInfo[];
  templateOptions: AgentInfo[];
  template?: AgentInfo;
  timeZone?: string;
  cloning: boolean;
  cloningSource?: AgentInfo;
  deployMode?: AGENT_DEPLOY_MODE;
  region?: AGENT_REGION;
  dirty: boolean;
  singleLang: boolean;

  // for clone mode
  cloneOrgOpts: SelectionOption[];
  cloneSourceOrg?: string;
  cloneAgentOpts: SelectionOption[];
  cloneSource?: string;
  hasInternalOrPrivateAgent: boolean;
  noteChecked: boolean;

  // for openapi mode
  openapiFile?: any;
}

export class AgentCreateModal extends React.Component<CreatePropsInfo, CreateStateInfo> {
  createInput: any;

  constructor(props: CreatePropsInfo) {
    super(props);

    let agentMode = AGENT_MODE.APPLICATION;
    let isService = false;
    let isInterface = false;
    let langs: string[] = [];
    let agentLang = AGENT_LAN.STRUCT;
    let singleLang = true;
    let privacy = AGENT_PRIVACY.PUBLIC;

    switch (props.type) {
      case 'component':
        agentMode = AGENT_MODE.LIBRARY;
        isInterface = false;
        langs = [];
        agentLang = AGENT_LAN.STRUCT;
        singleLang = false;
        break;
      case 'backend':
        isInterface = false;
        agentMode = AGENT_MODE.LIBRARY;
        isService = true;
        agentLang = AGENT_LAN.STRUCT;
        singleLang = false;
        break;
      case 'chatbot':
        agentMode = AGENT_MODE.APPLICATION;
        singleLang = false;
        langs = [AGENT_LAN.EN];
        agentLang = AGENT_LAN.STRUCT;
        break;
      case 'knowledge':
        agentMode = AGENT_MODE.KNOWLEDGE;
        agentLang = AGENT_LAN.STRUCT;
        singleLang = false;
        privacy = AGENT_PRIVACY.INTERNAL;
        break;
      default:
        break;
    }

    this.state = {
      agentMode,
      isService,
      isInterface,
      singleLang,
      privacy,
      hasInternalOrPrivateAgent: false,
      orgId: props.isCloning ? undefined : getOrgId(),
      name: '',
      createMode: 'create',
      backendProviderType: props.type === 'backend' && ProviderType.Postgrest || undefined,
      agentLanguage: agentLang,
      languages: langs,
      loading: false,
      dirty: false,
      templateOptions: [],
      cloning: false,
      deployMode: AGENT_DEPLOY_MODE.FRAMELY,
      region: AGENT_REGION.VIRGINIA,
      timeZone: DEFAULT_TIMEZONE,
      cloneOrgOpts: [],
      cloneAgentOpts: [],
      noteChecked: false
    };
  }

  componentDidMount() {
    this.onInit();
  }

  isCloning = () => {
    return this.props.isCloning || this.state.createMode === 'clone';
  }

  onInit = async () => {
    const { type } = this.props;
    const { backendProviderType, templateRes, languages, agentMode } = this.state;

    let nextLangs = languages;

    let orgOptions: SelectionOption[] | undefined;
    let res: any;

    try {

      if (this.isCloning()) {

        orgOptions = await getSelectionOptions(SELECTION_KEY.CLONE_DESTINATION, [{
          value: getStructAgentId() || '',
          value_type: 'agent'
        }]);

        const structId = getStructAgentId() || '';

        const langAgentOptions: any = structId &&
          await getSelectionOptions(
            SELECTION_KEY.LANGUAGE_AGENT_OF_STRUCT,
            [{ value: structId, value_type: 'agent' }]
          );

        nextLangs = langAgentOptions.filter((item: SelectionOption) =>
          item.value !== structId
        ).map((item: SelectionOption) =>
          getAgentLangFromLangLabel(item.label as string));

      } else {
        orgOptions = (await API.org.list() as any).map((item: any) => {
          return {
            value: item.id,
            value_type: 'agent',
            label: item.name
          };
        });
      }


      if (!templateRes || !templateRes.length) {
        res = await API.agent.templates()
          .then((response: any) =>
            (response || []).map(agentToLocal));
        this.setState({ templateRes: res });
      } else {
        res = templateRes;
      }
    } catch (e: any) {
      message.error(e.message);
      throw e;
    }


    switch (type) {
      case 'chatbot':
        res = res.filter((item: AgentInfo) =>
          item.mode === AGENT_MODE.APPLICATION);
        break;
      case 'component':
        res = res.filter((item: AgentInfo) =>
          item.mode === AGENT_MODE.LIBRARY && item.name !== 'ProviderLibrary');
        break;
      case 'backend':
        if (backendProviderType !== ProviderType.Native) {
          res = res.filter((item: AgentInfo) =>
            item.mode === AGENT_MODE.LIBRARY && item.name === 'ProviderLibrary');
        } else {
          res = res.filter((item: AgentInfo) =>
            item.mode === AGENT_MODE.LIBRARY && item.name !== 'ProviderLibrary');
        }
        break;
      case 'knowledge':
        res = [];
        break;
      default:
        break;
    }

    if (this.isCloning() && this.props.cloneAgent) {
      const from: AgentInfo = this.props.cloneAgent;

      const hasPrivate: any = await API.agent.hasInternalOrPrivateModule(from?.id || '')
        .catch((err: Error) => {
          message.error(err.message);
        });

      this.setState({
        orgOptions,
        cloning: true,
        hasInternalOrPrivateAgent: hasPrivate,
        name: `${from.name}Copy`,
        template: from.template && res.filter((item: AgentInfo) =>
          from.template &&
          item.id === from.template.agentId)[0],
        templateOptions: res,
        agentMode: from.mode || '',
        isService: from.isService,
        isServiceImplementation: from.isServiceImplementation,
        agentLanguage: from.language,
        privacy: from.privacy || '',
        timeZone: from.timeZone || DEFAULT_TIMEZONE,
        cloningSource: from,
        languages: from.language === AGENT_LAN.STRUCT ? nextLangs : [from.language || ''],
        backendProviderType: from.backendProviderType
      });

      return;
    }

    if (isTemplateOrg()) {
      return;
    }

    const lanFiltered = res.filter((item: AgentInfo) =>
      item.language === this.state.agentLanguage);

    const defaultTmp = lanFiltered
      .find((item: AgentInfo) =>
        item.name === DefaultTemplateName) || lanFiltered[0];

    this.setState({
      orgOptions,
      templateOptions: res,
      template: defaultTmp,
      agentMode: defaultTmp && defaultTmp.mode || agentMode
    });
  }

  updateCloneState = (from: AgentInfo) => {
    const { templateOptions, languages } = this.state;
    this.setState({
      name: `${from.name}Copy`,
      template: from.template && templateOptions.filter((item: AgentInfo) =>
        from.template &&
        item.id === from.template.agentId)[0],
      agentMode: from.mode || '',
      isService: from.isService,
      agentLanguage: from.language,
      privacy: from.privacy || '',
      timeZone: from.timeZone || DEFAULT_TIMEZONE,
      cloningSource: from,
      languages: from.language === AGENT_LAN.STRUCT ? languages : [from.language || ''],
      backendProviderType: from.backendProviderType
    });
  }

  onNameChange = (name: string) => {
    if (name.length > 60) {
      return;
    }

    this.setState({
      name,
      dirty: name ? true : false
    });
  };

  onCreate = debounce(async () => {
    const state = this.state;
    const props = this.props;

    const rules = {
      [localizedString('agent_create_org_placeholder')]: !state.orgId || state.orgId.length === 0,
      [localizedString('common_empty_name')]: !state.name || state.name.length === 0,
      [localizedString('common_invalid_name_length')]:
        state.name.length > 60 || state.name.length < 2,
      [localizedString('common_invalid_name')]: !agentLabelRegex.test(state.name),
      [localizedString('agent_language_hint')]: !state.agentLanguage,
      [localizedString('bot_language_add_hint', undefined, 'Please add at least one language!')]: !this.isCloning() && state.agentMode === AGENT_MODE.APPLICATION && state.languages.length < 1,
      [localizedString('agent_template_hint')]: props.type !== 'knowledge' && !this.isCloning() && !isTemplateOrg() && !state.template,
      [localizedString('openai_file_hint', undefined, 'OpenAPI spec file required')]: state.createMode === 'openapi' && !state.openapiFile
    };

    for (const rule in rules) {
      if (rules.hasOwnProperty(rule) && rules[rule]) {
        message.warning(rule);

        return;
      }
    }

    if (props.type === 'chatbot' && !state.noteChecked) {
      message.warning('Please read and check the agreement');
      return;
    }

    if (idCreateRegex.test(state.name)) {
      this.setState({
        loading: true
      });
      try {

        const tmp: AgentInfo | undefined = state.template;
        let res: any;
        if (this.isCloning() && state.cloningSource) {

          if (state.cloningSource.privacy === AGENT_PRIVACY.INTERNAL && state.cloningSource.orgId !== state.orgId) {
            message.warning('Internal privacy should not be cloned to other organizations');
            return;
          }

          res = await API.agent.clone({
            agentId: state.cloningSource.id || '',
            commitNumber: state.cloningSource.commitNumber || '',
            userId: state.cloningSource.userId || ''
          }, state.name, state.deployMode, state.region, state.orgId).then(agentToLocal);
        } else {

          if (state.singleLang) {

            res = await API.agent
              .createAgent({
                data: {
                  org_id: state.orgId,
                  name: state.name,
                  lang: state.languages[0],
                  mode: state.agentMode,
                  is_service: state.isService,
                  is_interface: state.isInterface,
                  is_service_implementation: state.isServiceImplementation,
                  backend_provider_type: state.backendProviderType,
                  privacy: state.privacy,
                  time_zone: state.timeZone,
                  deploy_mode: state.deployMode,
                  region: state.region,
                  template: tmp && {
                    agent_id: tmp.id,
                    user_id: tmp.userId,
                    commit_number: tmp.commitNumber
                  } || undefined
                },
                region: state.region
              }).then(agentToLocal);
          } else if (state.createMode === 'openapi') {


            res = await API.agent
              .createAgentGroupWithOpenapiSpec(state.orgId || '', this.getOrgName() || '', true, '', '', {
                name: state.name,
                lang: state.agentLanguage,
                mode: state.agentMode,
                is_service: state.isService,
                is_interface: state.isInterface,
                is_service_implementation: state.isServiceImplementation,
                backend_provider_type: state.backendProviderType,
                privacy: state.privacy,
                time_zone: state.timeZone,
                deploy_mode: state.deployMode,
                region: state.region,
                template: tmp && {
                  agent_id: tmp.id,
                  user_id: tmp.userId,
                  commit_number: tmp.commitNumber
                } || undefined
              }, state.languages, state.openapiFile).then(agentToLocal);

          } else {

            res = await API.agent
              .createAgentGroup(state.orgId || '', this.getOrgName() || '', true, '', '', {
                name: state.name,
                lang: state.agentLanguage,
                mode: state.agentMode,
                is_service: state.isService,
                is_interface: state.isInterface,
                is_service_implementation: state.isServiceImplementation,
                backend_provider_type: state.backendProviderType,
                privacy: state.privacy,
                time_zone: state.timeZone,
                deploy_mode: state.deployMode,
                region: state.region,
                template: tmp && {
                  agent_id: tmp.id,
                  user_id: tmp.userId,
                  commit_number: tmp.commitNumber
                } || undefined
              }, state.languages).then(agentToLocal);
          }
        }

        // props.$agent.handleAgent(agentToLocal(res));

        this.setState({
          loading: false,
          dirty: false
        });
        message.success(localizedString('common_create_success'));

        if (props.didCloseClick) {
          props.didCloseClick();
        }

        let orgName = getOrgName();

        if (this.isCloning()) {
          orgName = props.cloneToOrgName || '';
        }

        let lang = res.language;
        if (state.languages.includes(AGENT_LAN.EN)) {
          lang = 'en';
        } else if (state.languages.includes(AGENT_LAN.ZH)) {
          lang = 'zh';
        }

        if (props.type === 'knowledge') {
          props.history.push(`/org/${this.getOrgName()}/agent/${res.name}/${lang}/knowledge`);
        } else if (state.isService) {
          props.history.push(`/org/${this.getOrgName()}/agent/${res.name}/${lang}/service_schema`);
        } else {
          props.history.push(`/org/${this.getOrgName()}/agent/${res.name}/${lang}/type`);
        }

      } catch (err: any) {
        message.warning(err.message);
        this.setState({
          loading: false
        });
      }
    } else {
      message.warning(localizedString('common_invalid_name'));
    }
  }, 500);

  getOrgName = () => {
    const { orgOptions, orgId } = this.state;
    return orgOptions && orgOptions.find((item: SelectionOption) => item.value === orgId)?.label as string;
  }

  getAgentLanguageName(lanVal: string) {
    return localizedString(`agent_language_${lanVal}`);
  }

  render() {
    const state = this.state;
    const props = this.props;
    const { createMode, languages, isService, backendProviderType, templateOptions, orgId, orgOptions, singleLang,
      cloneOrgOpts,
      cloneAgentOpts,
      cloneSourceOrg,
      cloneSource,
      isInterface,
      isServiceImplementation,
      openapiFile
    } = state;

    const modeItems = [
      // { key: 'create', value: localizedString('agent_create_mode_create', undefined, 'New') },
      // { key: 'clone', value: localizedString('agent_create_mode_clone', undefined, 'Clone from') }
    ];

    if (props.type === 'component') {
      modeItems.push({ key: 'create', value: localizedString('agent_create_mode_create', undefined, 'New') });
      modeItems.push({
        key: 'openapi', value: localizedString('agent_create_mode_openapi', undefined, 'OpenAPI specs')
      });
    }

    return (
      <NiModal
        open={props.visible}
        title={localizedString(this.isCloning() ? 'agent_clone' : 'agent_create')
        }
        onOk={this.onCreate}
        okText={this.isCloning() ? localizedString('clone_project') : localizedString('common_create')}
        onCancel={() => {
          if (props.didCloseClick) {
            props.didCloseClick();
          }
        }}
        confirmLoading={state.loading}
      >
        <div className="bg-layout create-agent">
          <div className="page-body">
            <div className="body-content" style={{ backgroundColor: 'inherit' }}>
              <NiForm
                direction="vertical"
                items={[
                  modeItems.length && !props.isCloning && props.type !== 'knowledge' && {
                    key: 'create-mode',
                    label: localizedString('agent_create_mode', undefined, 'Create mode'),
                    labelExtra: <NiRadio
                      style={{
                        marginTop: 4
                      }}
                      value={createMode}
                      onChange={(e: any) => {

                        this.setState({
                          createMode: e.target.value,
                          isService: e.target.value === 'openapi' ? true : isService,
                          isInterface: e.target.value === 'openapi' ? true : isInterface,
                        }, () => {
                          if (e.target.value === 'clone') {
                            this.initCloneOrgOptions();
                          }
                        });
                      }}
                      keyValue={modeItems}
                    />,
                    content: createMode === 'clone' && <div className="org-agent-wrap">
                      <span className="org-wrap">
                        <NiSelect
                          placement="bottomLeft"
                          showSearch
                          filterOption={(input: string, option: any) => {
                            return option && input && (option.children as string).toLowerCase().indexOf(input.trim().toLowerCase()) !== -1 || false;
                          }}
                          listHeight={200}
                          placeholder={localizedString('org_select', undefined, 'Select org')}
                          key="select-import-org"
                          value={cloneSourceOrg}
                          onChange={(value: string) => {

                            if (value === cloneSourceOrg) {
                              return;
                            }

                            this.setState({ cloneSourceOrg: value, cloneSource: undefined }, this.initCloneAgentOptions);
                          }}
                        >
                          {
                            cloneOrgOpts.map((opt: SelectionOption) => <NiSelect.Option key={opt.value} value={opt.value}>{opt.label}</NiSelect.Option>)
                          }
                        </NiSelect>
                      </span>
                      <span className="divider-wrap"> / </span>
                      <span className="agent-wrap">
                        <NiSelect
                          style={{ width: '100%' }}
                          placement="bottomLeft"
                          showSearch
                          filterOption={(input: string, option: any) => {
                            return option && input && (option.children as string).toLowerCase().indexOf(input.trim().toLowerCase()) !== -1 || false;
                          }}
                          listHeight={200}
                          placeholder={localizedString('project_select', undefined, 'Select project')}
                          key="select-import-agent"
                          value={cloneSource}
                          onChange={(value: string) => {
                            if (value === cloneSource) {
                              return;
                            }
                            this.setState({ cloneSource: value }, () => {

                              const selected = cloneAgentOpts.find((opt: SelectionOption) => opt.value === value);

                              if (selected && selected.payload) {
                                this.updateCloneState(agentToLocal(selected.payload));
                              }
                            });
                          }}
                        >
                          {
                            cloneAgentOpts.map((opt: SelectionOption) => <NiSelect.Option key={opt.value} value={opt.value}>{opt.label}</NiSelect.Option>)
                          }
                        </NiSelect>
                      </span>
                    </div>
                  } || undefined,
                  createMode === 'openapi' && {
                    key: 'openapi-upload',
                    label: '',
                    content: <Upload.Dragger
                      // action={`/dialogflow/knowledge/upload`}
                      name="file"
                      customRequest={this.customUploadRequest}
                      onRemove={(file: UploadFile) => {
                        this.setState({ openapiFile: undefined });
                      }}

                      fileList={openapiFile && [{
                        uid: openapiFile.name,
                        name: openapiFile.name,
                        status: 'done'
                      }] || []}
                    >
                      <p className="ant-upload-text">Click or drag file to this area to upload</p>
                    </Upload.Dragger>
                  } || undefined,
                  {
                    key: 'name',
                    label: '',
                    content: <div className="org-agent-wrap">
                      {this.isCloning() && <><span className="org-wrap">
                        <div className="org-wrap-title" >
                          {localizedString('role_owner')}<HelperPopover style={{ marginLeft: 12 }} localizedKey="agent_create_helper_org" />
                        </div>
                        <NiSelect
                          disabled={!this.isCloning() || props.type === 'knowledge'}
                          key="org"
                          value={orgId}
                          placeholder={localizedString('agent_create_org_placeholder')}
                          onChange={(next: string) => {
                            this.setState({
                              orgId: next
                            });
                          }}
                        >
                          {
                            orgOptions?.map((item: SelectionOption) =>
                              <NiSelect.Option key={item.value} value={item.value}>
                                {
                                  item.label
                                }
                              </NiSelect.Option>)
                          }
                        </NiSelect>
                      </span>
                        <span className="divider-wrap"> / </span>
                      </>}
                      <span className="agent-wrap">
                        <div className="agent-wrap-title" >
                          {localizedString('agent_name')} <HelperPopover style={{ marginLeft: 12 }} localizedKey="agent_create_helper_label" />
                        </div>
                        <Input
                          ref={(createInput: any) => {
                            this.createInput = createInput;
                          }}
                          className="reset"
                          placeholder={localizedString('agent_name_placeholder')}
                          value={state.name}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            this.onNameChange(event.target.value)
                          }
                        />
                      </span>
                    </div>
                  }, props.type === 'backend' && {
                    key: 'provider-type',
                    label: localizedString('provider_type'),
                    labelExtra: <HelperPopover localizedKey="agent_create_provider_type" />,
                    content: <NiSelect
                      style={{ width: '100%' }}
                      disabled={this.isCloning()}
                      className="data-query-select"
                      placeholder={localizedString('data_query_type_helper_placeholder')}
                      value={(backendProviderType || null) as any}
                      onChange={(value: string) => {
                        this.setState({ backendProviderType: value as ProviderType }, this.onInit);
                      }}
                    >
                      <NiSelect.Option key={ProviderType.Restful} value={ProviderType.Restful}>
                        {localizedString('data_query_mode_restful')}
                      </NiSelect.Option>
                      <NiSelect.Option key={ProviderType.Postgrest} value={ProviderType.Postgrest}>
                        {localizedString('data_query_mode_database')}
                      </NiSelect.Option>
                      <NiSelect.Option key={ProviderType.Supabase} value={ProviderType.Supabase}>
                        {localizedString('data_query_mode_supabase')}
                      </NiSelect.Option>
                      {/* <NiSelect.Option key={ProviderType.GoogleSheets} value={ProviderType.GoogleSheets}>
                        {localizedString('data_query_mode_google_sheets')}
                      </NiSelect.Option> */}
                      <NiSelect.Option key={ProviderType.Native} value={ProviderType.Native}>
                        {localizedString('data_query_mode_native_provider')}
                      </NiSelect.Option>
                    </NiSelect>
                  } || undefined, {
                    key: 'privacy',
                    label: localizedString('agent_privacy'),
                    labelExtra: <HelperPopover localizedKey="agent_create_helper_privacy" />,
                    content: <NiSelect
                      disabled={this.isCloning()}
                      style={{ width: '100%' }}
                      value={state.privacy}
                      onChange={(val: string) => {
                        this.setState({ privacy: val });
                      }}
                    >
                      {Object.values(AGENT_PRIVACY).map((val: string) =>
                        <NiSelect.Option
                          title="Public project should not have internal or private modules"
                          disabled={props.type === 'knowledge' ? val === AGENT_PRIVACY.PUBLIC : (val === AGENT_PRIVACY.PUBLIC && this.state.hasInternalOrPrivateAgent)}
                          key={val}
                          value={val}
                        >
                          {localizedString(`agent_privacy_${val}`)}
                        </NiSelect.Option>
                      )}
                    </NiSelect>
                  }, !isTemplateOrg() && props.type !== 'knowledge' && {
                    key: 'template',
                    label: localizedString('agent_template'),
                    labelExtra: <HelperPopover localizedKey="agent_create_helper_template" />,
                    content: <NiSelect
                      disabled={this.isCloning()}
                      style={{ width: '100%' }}
                      value={state.template && state.template.id}
                      placeholder={localizedString('agent_template')}
                      onChange={(val: string) => {

                        const selected = state.templateOptions.find((item: AgentInfo) =>
                          item.id === val);

                        this.setState({
                          template: selected,
                          agentMode: selected && selected.mode || ''
                        });
                      }}
                    >
                      {
                        state.templateOptions
                          .filter((item: AgentInfo) =>
                            item.language === this.state.agentLanguage)
                          .map((tmp: AgentInfo, index: number) =>
                            <NiSelect.Option
                              key={tmp.id || String(index)}
                              value={tmp.id || ''}
                            >
                              {tmp.name}
                            </NiSelect.Option>
                          )
                      }
                    </NiSelect>
                  } || undefined,
                  state.agentMode === AGENT_MODE.APPLICATION && props.type !== 'knowledge' && {
                    key: 'deploy-mode',
                    label: localizedString('agent_deploy_mode'),
                    labelExtra: <HelperPopover localizedKey="agent_create_helper_deploy_mode" />,
                    content: <NiSelect
                      style={{ width: '100%' }}
                      value={state.deployMode}
                      onChange={(val: string) => {
                        this.setState({
                          deployMode: val as AGENT_DEPLOY_MODE
                        });
                      }}
                    >
                      <NiSelect.Option
                        key="framely"
                        value={AGENT_DEPLOY_MODE.FRAMELY}
                      >
                        {localizedString('agent_deploy_mode_framely')}
                      </NiSelect.Option>
                      <NiSelect.Option
                        key="private"
                        value={AGENT_DEPLOY_MODE.PRIVATE}
                      >
                        {localizedString('agent_deploy_mode_private')}
                      </NiSelect.Option>
                    </NiSelect>
                  } || undefined,
                  ((state.agentMode === AGENT_MODE.APPLICATION ||
                    (state.isService &&
                      !state.isInterface &&
                      state.backendProviderType === ProviderType.Postgrest)) &&
                    state.deployMode === AGENT_DEPLOY_MODE.FRAMELY
                  ) && props.type !== 'knowledge' && {
                    key: 'region',
                    label: localizedString('region'),
                    labelExtra: <HelperPopover localizedKey="agent_create_helper_region" />,
                    content: <NiSelect
                      style={{ width: '100%' }}
                      value={state.region}
                      onChange={(val: string) => {
                        this.setState({
                          region: val as AGENT_REGION
                        });
                      }}
                    >
                      {/* <NiSelect.Option
                        key="beijing"
                        value={AGENT_REGION.BJ}
                      >
                        Mainland China
                      </NiSelect.Option> */}
                      <NiSelect.Option
                        key="us"
                        value={AGENT_REGION.VIRGINIA}
                      >
                        North America
                      </NiSelect.Option>
                      {/* <NiSelect.Option
                        key="oregon"
                        value={AGENT_REGION.OREGON}
                      >
                        Oregon
                      </NiSelect.Option> */}
                    </NiSelect>
                  } || undefined,
                  !(isService && !state.isInterface) && props.type !== 'knowledge' &&
                  {
                    key: 'language-add',
                    label: localizedString('agent_language_add'),
                    labelExtra: state.agentMode === AGENT_MODE.APPLICATION && <Space>
                      <HelperPopover localizedKey="agent_language_popover" />
                      {/* <label>{localizedString('single_language_agent', undefined, 'Single Language')}</label>
                      <Switch
                        disabled={(!singleLang && languages.length > 1)}
                        // disabled
                        checked={singleLang} onChange={(checked: boolean) => {
                          this.setState({ singleLang: checked });

                          if (checked) {

                            if (languages && languages.length === 0) {

                              const val = languages[0];

                              const lanFiltered = templateOptions.filter((item: AgentInfo) =>
                                item.language === val);

                              const defaultTmp = lanFiltered
                                .find((item: AgentInfo) =>
                                  item.name === DefaultTemplateName) || lanFiltered[0];
                              this.setState({ agentLanguage: val, template: defaultTmp });
                            }

                          } else {
                            const val = AGENT_LAN.STRUCT;
                            const lanFiltered = templateOptions.filter((item: AgentInfo) =>
                              item.language === val);

                            const defaultTmp = lanFiltered
                              .find((item: AgentInfo) =>
                                item.name === DefaultTemplateName) || lanFiltered[0];
                            this.setState({ agentLanguage: val, template: defaultTmp });
                          }
                        }}
                      /> */}
                    </Space> || '',
                    content: <NiSelect
                      mode={singleLang ? undefined : 'multiple'}
                      disabled={this.isCloning()}
                      style={{ width: '100%' }}
                      placeholder={localizedString('agent_language_placeholder')}
                      value={languages as any}
                      onChange={(val: any) => {
                        if (singleLang) {

                          const lanFiltered = templateOptions.filter((item: AgentInfo) =>
                            item.language === val);

                          const defaultTmp = lanFiltered
                            .find((item: AgentInfo) =>
                              item.name === DefaultTemplateName) || lanFiltered[0];
                          this.setState({ agentLanguage: val, languages: [val], template: defaultTmp });
                        } else {
                          this.setState({ languages: val, agentLanguage: AGENT_LAN.STRUCT });
                        }
                      }}
                    >
                      {
                        this.isCloning() && <NiSelect.Option
                          key="struct"
                          value="struct"
                        // disabled={!!languages.find((lan: string) => lan === value)}
                        >
                          INTEGRATION
                        </NiSelect.Option>
                      }
                      {
                        ['en', 'zh'].map((value: string) =>
                          <NiSelect.Option
                            key={value}
                            value={value}
                          // disabled={!!languages.find((lan: string) => lan === value)}
                          >
                            {`${this.getAgentLanguageName(value)}(${value})`}
                          </NiSelect.Option>
                        )
                      }
                    </NiSelect>
                  } || undefined, props.type === 'component' && createMode !== 'openapi' && {
                    key: 'service',
                    label: localizedString('enable_service'),
                    labelExtra: <><Switch
                      disabled={this.isCloning() || isServiceImplementation}
                      checked={state.isService}
                      onChange={(checked: boolean) => {
                        this.setState({ isService: checked, isInterface: checked });
                      }}
                    />
                      <HelperPopover style={{ marginLeft: 12 }} localizedKey="enable_service_helper" />
                    </>
                  } || undefined,
                  props.type === 'component' && createMode !== 'openapi' && {
                    key: 'service-implementation',
                    label: 'Enable service Implementation',
                    labelExtra: <><Switch
                      disabled={this.isCloning() || isService}
                      checked={state.isServiceImplementation}
                      onChange={(checked: boolean) => {
                        this.setState({ isServiceImplementation: checked });
                      }}
                    />
                      {/* <HelperPopover style={{ marginLeft: 12 }} localizedKey="enable_service_helper" /> */}
                    </>
                  } || undefined,
                  props.type === 'chatbot' && {
                    key: 'time-zone',
                    label: localizedString('time_zone'),
                    labelExtra: <HelperPopover localizedKey="time_zone_helper" />,
                    content: <NiSelect
                      disabled={this.isCloning()}
                      value={state.timeZone}
                      style={{ width: '100%' }}
                      onChange={(value: string) => {
                        this.setState({ timeZone: value });
                      }}
                    >
                      {
                        tz.names().map((name: string) =>
                          <NiSelect.Option value={name} key={name}>
                            {
                              name
                            }
                          </NiSelect.Option>)
                      }
                    </NiSelect>
                  } || undefined,
                  // ,
                  // props.type === 'chatbot' && {
                  //   key: 'cui-level',
                  //   label: localizedString('cui_level', undefined, 'CUI level'),
                  //   labelExtra: <HelperPopover localizedKey="cui_level_helper" />,
                  //   content: <NiSelect
                  //     disabled
                  //     value={'2'}
                  //     style={{ width: '100%' }}
                  //     onChange={(value: string) => {
                  //       this.setState({timeZone: value });
                  //     }}
                  //   >
                  //     <NiSelect.Option value={'2'} key="2">
                  //       {
                  //         localizedString('cui_level_value', undefined, 'Level')
                  //       } 2
                  //     </NiSelect.Option>)
                  //   </NiSelect>
                  // } || undefined

                  props.type === 'chatbot' &&
                  {
                    key: 'note',
                    label: 'Agreement',
                    content: <>
                      <Paragraph
                        style={{
                          fontSize: '12px', // 调小字体
                          color: '#555', // 使用较暗的颜色
                          fontWeight: 'bold', // 加粗文本
                          backgroundColor: '#f9f9f9', // 设置背景色
                          padding: '10px', // 添加内边距
                          borderRadius: '4px', // 添加圆角
                          border: '1px solid #d9d9d9', // 添加边框
                        }}
                      >
                        For modules or public Chatbots, they are free for testing but can not be deployed.
                        <br />
                        For private or internal chatbots, You will be able to experience the full functionality for free for one month, after which we will start charging you a fee based on the number of such bots.For more pricing information, please check our <a href="https://bethere.ai" target="_blank">pricing page</a>.
                      </Paragraph>
                      <Checkbox onChange={(e: any) => {
                        this.setState({
                          noteChecked: e.target.checked
                        });
                      }}>
                        I have read and agree with the pricing.
                      </Checkbox>
                    </>
                  } || undefined
                ]}
              />
            </div>
          </div>
          <Prompt when={state.dirty} message={localizedString('common_save_success')} />
        </div >
      </NiModal >
    );
  }

  initCloneOrgOptions = async () => {
    const opts = await getSelectionOptions(SELECTION_KEY.CLONE_SOURCE);
    this.setState({ cloneOrgOpts: opts });
  }

  initCloneAgentOptions = async () => {
    const { type } = this.props;
    const { cloneSourceOrg } = this.state;

    let agentType = '';
    switch (type) {
      case 'chatbot':
        agentType = 'application';
        break;
      case 'component':
        agentType = 'component';
        break;
      case 'backend':
        agentType = 'backend';
        break;
      default:
        break;
    }
    const opts = await getSelectionOptions(SELECTION_KEY.CLONE_SOURCE, [{
      value: cloneSourceOrg || '',
      value_type: 'org',
    }, {
      value: agentType,
      value_type: 'agent_search'
    }]);
    this.setState({ cloneAgentOpts: opts });
  }

  customUploadRequest = ({
    action,
    data,
    file,
    filename,
    onError
  }: any) => {
    this.setState({ openapiFile: file });

  };

  showLangName = (item: string) => {
    return <span className="item-name">{`${this.getAgentLanguageName(item)}(${item})`}</span>;
  };

  showLangSuffix = (item: string, index: number) => {

    return (
      <div className="suffix-area">
        <div
          className={`admin-menu`}
        >
          {
            <DeleteOutlined
              className="delete-btn"
              onClick={() => {
                const langs: string[] = cloneDeep(this.state.languages);
                langs.splice(index, 1);
                this.setState({ languages: langs });
              }}
            />}
        </div>
      </div>
    );
  };
}
